import {
  deleteBodyType,
  editBodyType,
  addBodyType,
} from "@/app/lib/actions/body-types";
import { ItemContent, ItemTitle } from "@/components/ui/item";
import {
  ItemsListCard,
  ItemsListCardSkeleton,
} from "@/components/backoffice/items-list-card";
import { useBodyTypesQuery, invalidateBodyTypesQuery } from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import * as z from "zod";

import {
  addBodyTypeSchema,
  editBodyTypeSchema,
} from "@/lib/schemas/body-types";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { toast } from "sonner";

type BodyType = {
  id: number;
  name: string;
  isActive: boolean;
};

function BodyTypeItemsListCard() {
  const queryClient = useQueryClient();

  const bodyTypesQuery = useBodyTypesQuery({ sort: { name: "asc" } });
  const handleCreate = async (
    bodyTypeName: z.infer<typeof addBodyTypeSchema>,
  ) => {
    const response = await addBodyType(bodyTypeName);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Nouveau type de carrosserie créé.");
      invalidateBodyTypesQuery(queryClient);
    }
  };

  const handleUpdate = async (bodyType: z.infer<typeof editBodyTypeSchema>) => {
    const response = await editBodyType(bodyType);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Type de carrosserie modifié.");
      invalidateBodyTypesQuery(queryClient);
    }
  };

  const handleDelete = async (id: { id: number }) => {
    const response = await deleteBodyType(id);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Type de carrosserie supprimé.");
      invalidateBodyTypesQuery(queryClient);
    }
  };

  if (bodyTypesQuery.isPending) return <ItemsListCardSkeleton />;
  const bodyTypes = bodyTypesQuery.data?.bodyTypes ?? [];

  return (
    <ItemsListCard<BodyType>
      title="Types de carrosserie"
      items={bodyTypes}
      createSchema={addBodyTypeSchema}
      editSchema={editBodyTypeSchema}
      defaultCreateValues={{ name: "", isActive: true }}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      renderItem={(item) => (
        <>
          <ItemContent>
            <ItemTitle>{item.name}</ItemTitle>
          </ItemContent>
        </>
      )}
      renderFormFields={(form) => (
        <FieldGroup>
          <form.Field name="name">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Libellé</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={200}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>

          <form.Field name="isActive">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name}>Statut</FieldLabel>
                <Switch
                  id="switch"
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
              </Field>
            )}
          </form.Field>
        </FieldGroup>
      )}
    />
  );
}

export { BodyTypeItemsListCard };
